import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["targetContainer", "targetPlaceholder"]
  static values = {
    maxFiles: { type: Number, default: 10 }
  }

  connect() {
    this.files = []
    this.bindEvents()
    this.setupClickToUpload()
  }

  disconnect() {
    this.unbindEvents()
  }

  bindEvents() {
    this.dragEnterHandler = this.handleDragEnter.bind(this)
    this.dragOverHandler = this.handleDragOver.bind(this)
    this.dragLeaveHandler = this.handleDragLeave.bind(this)
    this.dropHandler = this.handleDrop.bind(this)

    this.element.addEventListener("dragenter", this.dragEnterHandler)
    this.element.addEventListener("dragover", this.dragOverHandler)
    this.element.addEventListener("dragleave", this.dragLeaveHandler)
    this.element.addEventListener("drop", this.dropHandler)
  }

  unbindEvents() {
    this.element.removeEventListener("dragenter", this.dragEnterHandler)
    this.element.removeEventListener("dragover", this.dragOverHandler)
    this.element.removeEventListener("dragleave", this.dragLeaveHandler)
    this.element.removeEventListener("drop", this.dropHandler)
  }

  setupClickToUpload() {
    // Find the file input
    this.fileInput = this.element.querySelector('input[type="file"]')
    if (!this.fileInput) return

    // Make the entire dropzone clickable
    this.targetPlaceholderTarget.addEventListener("click", () => {
      this.fileInput.click()
    })
  }

  handleDragEnter(e) {
    e.preventDefault()
    this.element.classList.add("border-blue-500")
  }

  handleDragOver(e) {
    e.preventDefault()
  }

  handleDragLeave(e) {
    e.preventDefault()
    this.element.classList.remove("border-blue-500")
  }

  handleDrop(e) {
    e.preventDefault()
    this.element.classList.remove("border-blue-500")
    const droppedFiles = Array.from(e.dataTransfer.files)
    this.handleFiles(droppedFiles)
  }

  loadFiles(event) {
    const selectedFiles = Array.from(event.target.files)
    this.handleFiles(selectedFiles)
  }

  handleFiles(newFiles) {
    // Filter for images and respect max files limit
    const imageFiles = newFiles.filter(file => file.type.startsWith('image/'))
    const availableSlots = this.maxFilesValue - this.files.length
    const filesToAdd = imageFiles.slice(0, availableSlots)

    if (filesToAdd.length > 0) {
      // Add new files
      this.files = [...this.files, ...filesToAdd]
      
      // Update UI
      this.updatePreview()
      
      // Show preview container, hide placeholder
      this.targetContainerTarget.classList.remove("hidden")
      this.targetPlaceholderTarget.classList.add("hidden")

      // Update the file input with the current files
      const dataTransfer = new DataTransfer()
      this.files.forEach(file => dataTransfer.items.add(file))
      this.fileInput.files = dataTransfer.files

      // Dispatch event for cost calculator
      this.dispatch("filesChanged", { 
        detail: { 
          files: this.files.map(file => ({ file }))
        } 
      })
    }
  }

  removeFile(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.files.splice(index, 1)
    
    if (this.files.length === 0) {
      this.targetContainerTarget.classList.add("hidden")
      this.targetPlaceholderTarget.classList.remove("hidden")
    } else {
      this.updatePreview()
    }

    // Update the file input with remaining files
    const dataTransfer = new DataTransfer()
    this.files.forEach(file => dataTransfer.items.add(file))
    this.fileInput.files = dataTransfer.files

    // Dispatch event for cost calculator
    this.dispatch("filesChanged", { 
      detail: { 
        files: this.files.map(file => ({ file }))
      } 
    })
  }

  updatePreview() {
    this.targetContainerTarget.innerHTML = this.files.map((file, index) => `
      <div class="preview-card relative bg-gray-800 rounded-lg overflow-hidden m-2" style="width: 150px; height: 150px;">
        <div class="w-full h-full">
          <img src="${URL.createObjectURL(file)}" 
               class="w-full h-full object-cover" 
               alt="${file.name}">
        </div>
        <button type="button"
                data-action="click->upscale-dropzone#removeFile"
                data-index="${index}"
                class="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 
                       flex items-center justify-center shadow-lg
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
          ×
        </button>
        <div class="absolute bottom-0 left-0 right-0 bg-gray-800 bg-opacity-75 text-white p-2 text-xs truncate">
          ${file.name}
        </div>
      </div>
    `).join('')
  }
} 
